// Chart › Finance › Forecast (`/c/{slug}/chart/finance/forecast`; 11 §Planning "Forecast", M5-03
// `forecasts.list`; Chart UX pass 2026-10-21): the month's projection per company, team and seat,
// from the latest snapshot the hourly job takes once a company-local day: actual to date, the run
// rate of metered spend, the fixed costs still due (pay, recurring expenses) and the projected
// month end against the budget, as four compact tiles over one table whose "Projected" column is
// an inline bar against the budget. Totals follow the finance matrix (the API filters the rows).
// Two calls on load: the forecast and the org chart (names; the chart and sidebar share it).
import {
  Badge,
  buttonVariants,
  cn,
  EmptyState,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, Building2Icon, TrendingUpIcon, UsersIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { SeatAvatar } from "../chart/chart-parts";
import type { CompanyAccess } from "../company";
import { formatDate, formatMoney } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { UsedBar } from "./budget-vs-actual";
import {
  FinancePage,
  MonthSelect,
  monthKeyOf,
  monthLabel,
  monthsAround,
  useOrgNames,
} from "./finance-parts";
import { forecastQuery } from "./finance-queries";

type Row = Awaited<ReturnType<Api["client"]["forecasts"]["list"]>>["rows"][number];

const KIND_RANK = { company: 0, team: 1, seat: 2 } as const;

/** Projected month end against the budget. */
export function projection(r: Pick<Row, "projected_minor" | "budget_minor">) {
  if (r.budget_minor === null) return { label: "No budget", tone: "neutral" as const };
  if (r.projected_minor > r.budget_minor) return { label: "Heading over", tone: "danger" as const };
  if (r.projected_minor >= r.budget_minor * 0.8)
    return { label: "Close to budget", tone: "warning" as const };
  return { label: "On track", tone: "success" as const };
}

export function ForecastScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const current = monthKeyOf(new Date());
  const months = useMemo(() => monthsAround(current, 12, 0), [current]);
  const [month, setMonth] = useState(current);
  const view = useQuery(forecastQuery(api, company.id, month));
  const names = useOrgNames(api, company);
  const rows = [...(view.data?.rows ?? [])].sort(
    (a, b) =>
      KIND_RANK[a.subject_kind] - KIND_RANK[b.subject_kind] ||
      names.nodeName(a.subject_id).localeCompare(names.nodeName(b.subject_id)),
  );
  const root = rows.find((r) => r.subject_kind === "company");
  const currency = root?.currency ?? rows[0]?.currency ?? "USD";
  const money = (minor: number | null) => (minor === null ? "—" : formatMoney(minor, currency));
  const ratio = (r: Row) =>
    r.budget_minor === null || r.budget_minor === 0
      ? null
      : String(r.projected_minor / r.budget_minor);

  return (
    <FinancePage
      company={company}
      where="chart"
      title="Forecast"
      icon={TrendingUpIcon}
      screen="finance-forecast"
      {...(view.data ? { count: rows.length } : {})}
      toolbar={
        <>
          <MonthSelect value={month} onChange={setMonth} months={months} />
          <span className="text-xs text-muted-foreground" data-slot="forecast-as-of">
            {view.data?.as_of ? `As of ${formatDate(view.data.as_of)} · ` : ""}
            {currency} · run rate is metered spend so far, scaled to the month
          </span>
        </>
      }
    >
      {view.isPending ? (
        <Loading label="Loading the forecast" rows={5} />
      ) : view.isError ? (
        <LoadError what="The forecast" error={view.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="card"
          icon={TrendingUpIcon}
          title={`No forecast for ${monthLabel(month)} yet`}
          description="A snapshot is taken once a day by the forecast job. The first one appears after it runs this month."
        />
      ) : (
        <>
          {root ? (
            <StatGrid
              dense
              data-slot="forecast-totals"
              aria-label={`${monthLabel(month)} forecast`}
            >
              <StatTile
                size="sm"
                label="Actual to date"
                value={money(root.actual_minor)}
                detail={monthLabel(month)}
              />
              <StatTile
                size="sm"
                label="Projected"
                value={money(root.projected_minor)}
                detail={projection(root).label}
              />
              <StatTile
                size="sm"
                label="Still due"
                value={money(root.committed_minor)}
                detail="Pay and recurring"
              />
              <StatTile
                size="sm"
                label="Budget"
                value={money(root.budget_minor)}
                detail={
                  root.budget_minor === null ? (
                    "No company budget"
                  ) : (
                    <UsedBar
                      ratio={ratio(root)}
                      tone={projection(root).tone}
                      label={`Projected ${money(root.projected_minor)} of ${money(root.budget_minor)}`}
                    />
                  )
                }
              />
            </StatGrid>
          ) : null}
          <Table data-slot="forecast">
            <TableCaption className="sr-only">
              {monthLabel(month)} in {currency}. Run rate is metered spend so far, scaled to the
              whole month.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Company, team or seat</TableHead>
                <TableHead scope="col" className="text-right">
                  Actual
                </TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  Run rate
                </TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  Still due
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Projected
                </TableHead>
                <TableHead scope="col" className="text-right max-sm:hidden">
                  Budget
                </TableHead>
                <TableHead scope="col" className="w-48 max-md:hidden">
                  Of budget
                </TableHead>
                <TableHead scope="col">Outlook</TableHead>
                <TableHead scope="col" className="w-10">
                  <span className="sr-only">Open</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const p = projection(r);
                const seat = r.subject_kind === "seat" ? names.seatOf(r.subject_id) : undefined;
                const name = names.nodeName(r.subject_id);
                return (
                  <TableRow
                    key={r.id}
                    data-subject={r.subject_id}
                    data-kind={r.subject_kind}
                    className={cn("group/row", r.subject_kind === "company" && "bg-sunken")}
                  >
                    <TableHead
                      scope="row"
                      className={cn(
                        "py-1.5 font-normal",
                        r.subject_kind === "team" && "pl-5",
                        r.subject_kind === "seat" && "pl-10",
                        r.subject_kind !== "seat" && "font-medium",
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {r.subject_kind === "company" ? (
                          <Building2Icon aria-hidden="true" className="size-4 text-faint" />
                        ) : r.subject_kind === "team" ? (
                          <UsersIcon aria-hidden="true" className="size-4 text-faint" />
                        ) : seat ? (
                          <SeatAvatar seat={seat} size="sm" />
                        ) : null}
                        {r.subject_kind === "seat" ? (
                          <Link
                            to="/c/$slug/kpis/chart/seat/$id"
                            params={{ slug: company.slug, id: r.subject_id }}
                            search={keepFocus}
                            className="flex min-w-0 items-baseline gap-1.5 underline-offset-4 hover:underline"
                          >
                            {seat?.holders.length ? (
                              <>
                                <span className="truncate font-medium">
                                  {seat.holders.map((h) => h.display_name).join(", ")}
                                </span>
                                <span className="truncate text-xs text-muted-foreground">
                                  {name}
                                </span>
                              </>
                            ) : (
                              <span className="truncate">{name}</span>
                            )}
                          </Link>
                        ) : (
                          <span className="truncate">{name}</span>
                        )}
                      </span>
                    </TableHead>
                    <TableCell className="py-1.5 text-right tabular-nums">
                      {money(r.actual_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-md:hidden">
                      {money(r.run_rate_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-md:hidden">
                      {money(r.committed_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right font-medium tabular-nums">
                      {money(r.projected_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-sm:hidden">
                      {money(r.budget_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 max-md:hidden">
                      <UsedBar
                        ratio={ratio(r)}
                        tone={p.tone}
                        label={`${name}: projected ${money(r.projected_minor)} of ${money(r.budget_minor)}`}
                      />
                    </TableCell>
                    <TableCell className="py-1.5">
                      <Badge variant={p.tone}>{p.label}</Badge>
                    </TableCell>
                    <TableCell className="py-1.5">
                      {r.subject_kind === "seat" ? (
                        <Link
                          to="/c/$slug/kpis/chart/seat/$id"
                          params={{ slug: company.slug, id: r.subject_id }}
                          search={keepFocus}
                          aria-label={`Open ${name}`}
                          title="Open seat"
                          className={cn(
                            buttonVariants({ variant: "ghost", size: "icon-xs" }),
                            "opacity-0 transition-opacity duration-(--dur-fast) group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100",
                          )}
                        >
                          <ArrowUpRightIcon aria-hidden="true" />
                        </Link>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </FinancePage>
  );
}
